export const POOL_BASE_PATH = '/pool';
export const ADMIN_BASE_PATH = '/admin';

export const shellBasePath = (isPool: boolean): string =>
  isPool ? POOL_BASE_PATH : ADMIN_BASE_PATH;

export const accountDetailPath = (
  basePath: string,
  accountId: string
): string => `${basePath}/account/${accountId}`;

export const accountDetailBaseUrl = (basePath: string): string =>
  `${basePath}/account/:accountId`;

export const accountsListPath = (basePath: string): string =>
  `${basePath}/accounts`;

export const claimDetailPath = (
  basePath: string,
  claimId: string,
  tab = 'summary'
): string => `${basePath}/claims/detail/${claimId}/${tab}`;

export const claimDetailBaseUrl = (basePath: string): string =>
  `${basePath}/claims/detail/:claimId`;

/**
 * The full-page invoice review/split screen, reached from an invoice's kebab, a row click on a
 * New invoice, or the Verify task. Deliberately a sibling of the claim detail tabs rather than a
 * tab itself: `ClaimDetailSubrouter` always renders the tab chrome and rail, and the review
 * screen owns its own header and footer edge to edge.
 */
export const invoiceReviewPath = (
  basePath: string,
  claimId: string,
  invoiceId: string
): string =>
  `${basePath}/claims/detail/${claimId}/invoices/${invoiceId}/review`;

export const invoiceReviewBaseUrl = (basePath: string): string =>
  `${basePath}/claims/detail/:claimId/invoices/:invoiceId/review`;

/** Where the review screen was opened from, carried in router state so it can go back there. */
export interface InvoiceReviewReturnTo {
  readonly pathname: string;
  readonly search: string;
}

export interface InvoiceReviewState {
  readonly returnTo?: InvoiceReviewReturnTo;
}

/**
 * The review screen's location, remembering the page that opened it.
 *
 * <p>It is opened from three lists -- a claim's own Invoices tab, Claims > Invoices and
 * My Invoices -- and the URL says only which claim the invoice is on. Without this, leaving the
 * review always went to the claim's tab, stranding an adjuster who started from their queue.
 */
export const invoiceReviewLocation = (
  basePath: string,
  claimId: string,
  invoiceId: string,
  from: { readonly pathname: string; readonly search?: string }
): { pathname: string; state: InvoiceReviewState } => ({
  pathname: invoiceReviewPath(basePath, claimId, invoiceId),
  state: { returnTo: { pathname: from.pathname, search: from.search ?? '' } },
});

const INVOICE_LIST_LABELS: Readonly<Record<string, string>> = {
  invoices: 'Invoices',
  myInvoices: 'My Invoices',
};

/**
 * The name of the cross-claim invoice list at `pathname`, or null when it is not one -- a
 * claim's own Invoices tab included, which the review names by its claim instead.
 */
export const invoiceListLabel = (pathname: string): string | null => {
  const match = /\/claims(?:\/list)?\/(invoices|myInvoices)\/?$/.exec(pathname);
  return match ? INVOICE_LIST_LABELS[match[1]] : null;
};

export const claimsTableSearchPath = (
  isPool: boolean,
  accountId: string
): string =>
  isPool
    ? `${POOL_BASE_PATH}/claims/list?search=${accountId}`
    : `${ADMIN_BASE_PATH}/claims?search=${accountId}`;

export const claimsListBaseUrl = (isPool: boolean): string =>
  isPool ? `${POOL_BASE_PATH}/claims/list` : `${ADMIN_BASE_PATH}/claims`;

// `allMarkets=1` marks this as a cross-page DEEP LINK (from an account,
// policy, quote, binder, etc.) rather than in-tab typing on the Retail tab.
// The agencies subrouter reads this marker to decide whether the Retail
// listing should span retail + wholesale (deep link) or stay retail-only
// (plain in-tab search). See CowbellAgenciesSubrouter.
export const AGENCIES_ALL_MARKETS_PARAM = 'allMarkets';

export const agenciesSearchPath = (
  basePath: string,
  agencyId: string
): string =>
  `${basePath}/agencies?search=${agencyId}&${AGENCIES_ALL_MARKETS_PARAM}=1`;

export const agenciesBaseUrl = (basePath: string): string =>
  `${basePath}/agencies`;
